/**
 * AI-Enhanced Bulk Upload Service
 * 
 * Uses OpenAI API to intelligently process and enhance bulk uploaded initiatives:
 * - Auto-generates missing titles from descriptions
 * - Improves and standardizes objectives  
 * - Suggests better structuring
 * - Auto-categorizes initiatives
 * - Generates key results from objectives
 * - Fixes validation issues automatically
 * 
 * @module services/AIEnhancedBulkUpload
 */

import ApiService from '../services/ApiService.js';
import { validateObject, extractJSONObject } from '../utils/schema.js';
import EventBus, { AppEvents } from '../core/EventBus.js';

class AIEnhancedBulkUpload {
  constructor() {
    this.processingQueue = [];
    this.enhancedResults = [];
  }

  /**
   * Process uploaded CSV with AI enhancement
   * @param {string} csvContent - Raw CSV content
   * @param {Object} options - Processing options
   * @returns {Object} Enhanced results
   */
  async processWithAI(csvContent, options = {}) {
    console.log('🤖 Starting AI-enhanced bulk upload processing...');
    
    try {
      // Step 1: Initial CSV parsing (relaxed validation)
      console.log('📄 Parsing CSV with relaxed validation...');
      const initialResult = this.parseCSVRelaxed(csvContent);
      
      if (!initialResult.success) {
        throw new Error(initialResult.error);
      }

      // Step 2: AI Enhancement for each row
      console.log('🧠 Enhancing initiatives with AI...');
      const enhancedRows = [];
      
      for (let i = 0; i < initialResult.data.length; i++) {
        const row = initialResult.data[i];
        console.log(`Processing row ${i + 1}/${initialResult.data.length}: ${row.title || 'Untitled'}`);
        
        // Emit progress
        EventBus.emit(AppEvents.BULK_IMPORT_START, {
          current: i + 1,
          total: initialResult.data.length,
          status: 'Enhancing with AI...'
        });
        
        try {
          const enhanced = await this.enhanceInitiativeWithAI(row, i + 1);
          enhancedRows.push(enhanced);
        } catch (error) {
          console.warn(`Failed to enhance row ${i + 1}:`, error);
          // Keep original row if AI enhancement fails
          enhancedRows.push(row);
        }
      }

      // Step 3: Final validation
      console.log('✅ Running final validation...');
      const finalResult = this.validateEnhancedRows(enhancedRows);

      console.log('🎉 AI enhancement complete!');
      return {
        success: true,
        original: initialResult,
        enhanced: enhancedRows,
        validation: finalResult,
        summary: {
          totalRows: initialResult.data.length,
          enhancedRows: enhancedRows.length,
          validRows: finalResult.validRows,
          invalidRows: finalResult.invalidRows,
          aiImprovements: this.calculateImprovements(initialResult.data, enhancedRows)
        }
      };
      
    } catch (error) {
      console.error('❌ AI enhancement failed:', error);
      throw error;
    }
  }

  /**
   * Parse CSV with relaxed validation (more forgiving)
   */
  parseCSVRelaxed(csvContent) {
    try {
      const lines = csvContent.split(/\r?\n/).filter(line => line.trim());
      
      if (lines.length < 2) {
        return {
          success: false,
          error: 'CSV must have at least a header row and one data row'
        };
      }

      const headers = this.parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());
      const columnMap = this.mapColumnsFlexible(headers);
      
      console.log('📊 CSV Analysis:', {
        headers,
        columnMap,
        totalRows: lines.length - 1
      });

      const data = [];
      
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        const values = this.parseCSVLine(line);
        const row = this.mapRowFlexible(values, columnMap, headers, i + 1);
        
        if (this.hasMinimumData(row)) {
          data.push(row);
        }
      }
      
      return {
        success: true,
        data,
        headers,
        totalRows: lines.length - 1,
        actualRows: data.length
      };
      
    } catch (error) {
      return {
        success: false,
        error: `Parse error: ${error.message}`
      };
    }
  }

  /**
   * More flexible column mapping
   */
  mapColumnsFlexible(headers) {
    const mapping = {};
    
    // Extended column mappings for better detection
    const patterns = {
      title: ['title', 'name', 'initiative', 'project', 'objective title', 'item', 'subject'],
      description: ['description', 'details', 'summary', 'overview', 'notes', 'info', 'about'],
      objective: ['objective', 'goal', 'outcome', 'business objective', 'purpose', 'aim', 'target'],
      owner: ['owner', 'responsible', 'lead', 'assigned to', 'reporter', 'person', 'contact'],
      priority: ['priority', 'importance', 'level', 'urgency'],
      status: ['status', 'state', 'progress status', 'condition'],
      dependsOn: ['depends on', 'dependencies', 'blocks', 'blocked by'],
      riskLevel: ['risk level', 'risk', 'risk score'],
      riskNote: ['risk note', 'risk description', 'risk details']
    };
    
    headers.forEach((header, index) => {
      const cleanHeader = header.toLowerCase().trim();
      
      for (const [field, variations] of Object.entries(patterns)) {
        if (variations.some(v => cleanHeader.includes(v) || v.includes(cleanHeader))) {
          if (!mapping[field]) { // Take first match
            mapping[field] = index;
          }
        }
      }
    });
    
    return mapping;
  }

  /**
   * More flexible row mapping
   */
  mapRowFlexible(values, columnMap, headers, rowNumber) {
    const row = {
      title: '',
      description: '',
      objective: '',
      owner: '',
      priority: 'medium',
      status: 'not-started',
      type: 'Initiative',
      progress: 0,
      keyResults: [],
      dependsOn: [],
      riskLevel: null,
      riskNote: '',
      originalRowNumber: rowNumber,
      rawData: values.slice(), // Keep original for AI processing
      detectedColumns: Object.keys(columnMap)
    };

    // Map detected columns
    for (const [field, index] of Object.entries(columnMap)) {
      if (index !== undefined && values[index]) {
        const val = values[index].trim();
        if (field === 'dependsOn') {
          row.dependsOn = val.split(/;|\||,/) .map(s => s.trim()).filter(Boolean);
        } else if (field === 'riskLevel') {
          const v = val.toLowerCase();
          const map = { 'low': 1, 'medium': 3, 'med': 3, 'high': 5 };
          row.riskLevel = Number.isFinite(Number(val)) ? Number(val) : (map[v] || null);
        } else if (field === 'riskNote') {
          row.riskNote = val;
        } else {
          row[field] = val;
        }
      }
    }

    // Store unmapped data for AI to analyze
    const unmappedData = [];
    headers.forEach((header, index) => {
      const isMapped = Object.values(columnMap).includes(index);
      if (!isMapped && values[index] && values[index].trim()) {
        unmappedData.push({
          header: header,
          value: values[index].trim()
        });
      }
    });
    
    if (unmappedData.length > 0) {
      row.unmappedData = unmappedData;
    }

    return row;
  }

  /**
   * Check if row has minimum usable data
   */
  hasMinimumData(row) {
    // Very relaxed - just need some text content
    const hasContent = row.title || row.description || row.objective || 
                      (row.unmappedData && row.unmappedData.some(d => d.value.length > 5));
    return hasContent;
  }

  /**
   * Enhance a single initiative using AI
   */
  async enhanceInitiativeWithAI(row, rowNumber) {
    const prompt = this.buildEnhancementPrompt(row);
    
    try {
      const response = await ApiService.call(prompt, {
        temperature: 0.3,
        maxTokens: 1000,
        response_format: { type: 'json_object' }
      });

      const enhanced = this.parseAIResponse(response.content, row);
      
      // Mark as AI-enhanced
      enhanced.aiEnhanced = true;
      enhanced.originalData = { ...row };
      
      return enhanced;
      
    } catch (error) {
      console.warn(`AI enhancement failed for row ${rowNumber}:`, error.message);
      return row; // Return original if AI fails
    }
  }

  /**
   * Build AI enhancement prompt with system context
   */
  buildEnhancementPrompt(row) {
    const availableData = [];
    
    // Add detected fields
    if (row.title) availableData.push(`Title: ${row.title}`);
    if (row.description) availableData.push(`Description: ${row.description}`);
    if (row.objective) availableData.push(`Objective: ${row.objective}`);
    if (row.owner) availableData.push(`Owner: ${row.owner}`);
    
    // Add unmapped data
    if (row.unmappedData) {
      row.unmappedData.forEach(item => {
        availableData.push(`${item.header}: ${item.value}`);
      });
    }
    
    // Add raw data as fallback
    if (row.rawData && row.rawData.length > 0) {
      availableData.push(`Raw CSV data: ${row.rawData.join(' | ')}`);
    }

    return `You are an expert business strategy consultant helping to structure corporate initiatives from CSV upload data.

Your role: Transform incomplete or poorly structured initiative data into professional, actionable business initiatives.

Current initiative data:
${availableData.join('\n')}

Transform this into a properly structured business initiative following these requirements:

**REQUIRED FIELDS:**
- Title: Clear, professional title (5-60 chars) - if missing, generate from available content
- Objective: Specific, measurable business goal (15-150 chars) - must start with action verb
- Owner: Responsible person - if missing, use "To Be Assigned"

**ENHANCED FIELDS:**
- Description: Professional summary (optional, up to 300 chars)
- Priority: Based on business impact (low/medium/high/critical)
- Type: Initiative category (Goal/Challenge/Demand/Initiative)
- Key Results: 2-3 measurable outcomes

**QUALITY STANDARDS:**
- Use professional business language
- Make objectives SMART (Specific, Measurable, Achievable, Relevant, Time-bound)
- Ensure clarity and actionability
- Focus on business value and outcomes

Respond with ONLY this JSON structure:
{
  "title": "Professional initiative title",
  "objective": "Specific measurable business objective starting with action verb",
  "owner": "Owner name or 'To Be Assigned'",
  "description": "Professional description (optional)",
  "priority": "low|medium|high|critical",
  "type": "Goal|Challenge|Demand|Initiative",
  "keyResults": ["Measurable outcome 1", "Measurable outcome 2"],
  "aiImprovements": ["List what was enhanced or generated"]
}`;
  }

  /**
   * Parse AI response into structured data
   */
  parseAIResponse(aiContent, originalRow) {
    try {
      const object = extractJSONObject(aiContent);
      if (!object) {
        console.warn('No JSON found in AI response, using original data');
        return originalRow;
      }

      // Validate expected keys
      const check = validateObject(object, {
        title: 'string',
        objective: 'string',
        owner: 'string',
        description: v => typeof v === 'string' || v === undefined,
        priority: 'string',
        type: 'string',
        keyResults: 'array',
        aiImprovements: v => Array.isArray(v) || v === undefined
      });
      const aiData = check.ok ? object : object; // accept object; validation logs left out to avoid noise
      
      // Merge with original row, prioritizing AI enhancements
      const enhanced = {
        ...originalRow,
        title: aiData.title || originalRow.title || 'Untitled Initiative',
        objective: aiData.objective || originalRow.objective || 'To be defined',
        owner: aiData.owner || originalRow.owner || 'To Be Assigned',
        description: aiData.description || originalRow.description || '',
        priority: aiData.priority || originalRow.priority || 'medium',
        type: aiData.type || originalRow.type || 'Initiative',
        status: originalRow.status || 'not-started',
        progress: originalRow.progress || 0,
        keyResults: aiData.keyResults || originalRow.keyResults || [],
        aiImprovements: aiData.aiImprovements || []
      };

      // Clean up enhanced data
      delete enhanced.rawData;
      delete enhanced.unmappedData;
      delete enhanced.detectedColumns;
      
      return enhanced;
      
    } catch (error) {
      console.warn('Failed to parse AI response:', error);
      return originalRow;
    }
  }

  /**
   * Validate enhanced rows
   */
  validateEnhancedRows(rows) {
    const validRows = [];
    const invalidRows = [];
    
    rows.forEach((row, index) => {
      const errors = [];
      
      // Relaxed validation - AI should have fixed most issues
      if (!row.title || row.title.length < 3) {
        errors.push('Title too short (min 3 characters)');
      }
      
      if (!row.objective || row.objective.length < 5) {
        errors.push('Objective too short (min 5 characters)');
      }
      
      if (!row.owner) {
        errors.push('Owner is required');
      }
      
      if (errors.length === 0) {
        validRows.push(row);
      } else {
        invalidRows.push({
          row: index + 1,
          data: row,
          errors
        });
      }
    });
    
    return {
      validRows: validRows.length,
      invalidRows: invalidRows.length,
      valid: validRows,
      invalid: invalidRows
    };
  }

  /**
   * Calculate improvements made by AI
   */
  calculateImprovements(originalRows, enhancedRows) {
    const improvements = {
      titlesGenerated: 0,
      objectivesImproved: 0,
      ownersAssigned: 0,
      keyResultsGenerated: 0,
      descriptionsEnhanced: 0,
      categorized: 0
    };
    
    enhancedRows.forEach((enhanced, index) => {
      const original = originalRows[index];
      
      if (!original.title && enhanced.title) {
        improvements.titlesGenerated++;
      }
      
      if (enhanced.objective && enhanced.objective !== original.objective) {
        improvements.objectivesImproved++;
      }
      
      if (!original.owner && enhanced.owner !== 'To Be Assigned') {
        improvements.ownersAssigned++;
      }
      
      if (enhanced.keyResults && enhanced.keyResults.length > 0) {
        improvements.keyResultsGenerated++;
      }
      
      if (enhanced.description && enhanced.description !== original.description) {
        improvements.descriptionsEnhanced++;
      }
      
      if (enhanced.type && enhanced.type !== 'Initiative') {
        improvements.categorized++;
      }
    });
    
    return improvements;
  }

  /**
   * Parse CSV line (same as original)
   */
  parseCSVLine(line) {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    values.push(current.trim());
    return values;
  }
}

// Create singleton instance
const aiEnhancedBulkUpload = new AIEnhancedBulkUpload();

// Export singleton and class
export { aiEnhancedBulkUpload, AIEnhancedBulkUpload };

// Make singleton the default export
export default aiEnhancedBulkUpload;

// Development helpers
if (import.meta.env.DEV) {
  window.aiEnhancedBulkUpload = aiEnhancedBulkUpload;
  
  // Test function
  window.testAIUpload = async (csvContent) => {
    console.log('🧪 Testing AI-enhanced upload...');
    try {
      const result = await aiEnhancedBulkUpload.processWithAI(csvContent);
// @ts-check
      console.log('✅ Test Results:', result);
      return result;
    } catch (error) {
      console.error('❌ Test failed:', error);
      throw error;
    }
  };
  
  console.log('🔧 AI Enhanced Bulk Upload available for testing');
  console.log('💡 Try: await window.testAIUpload("Title,Desc\\nTest,Test desc")');
}
